/**
 * Computed Fields Engine
 * 
 * Evaluates cross-section aggregations defined in form_schema.computed_fields.
 * Supported types: count, sum, average, min, max, percentage (pass_rate), concat.
 * 
 * Computed fields are recalculated on every formData change and displayed
 * in the form summary or injected back into formData for downstream use.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface WeightedSource {
  /** Section key to score */
  section_key: string;
  /** Field key within the section (scores from this field) */
  field_key?: string;
  /** Weight multiplier for this source (default 1) */
  weight: number;
  /** Maximum possible score for this source (used to normalize) */
  max_score?: number;
}

export interface PassFailCondition {
  /** Operator for comparing the computed value */
  operator: '>=' | '>' | '<=' | '<' | '=' | '!=';
  /** Threshold value */
  threshold: number;
  /** Result label when condition is met */
  pass_label?: string;
  /** Result label when condition is NOT met */
  fail_label?: string;
}

export interface ComputedFieldDef {
  key: string;
  label: string;
  type: 'count' | 'sum' | 'average' | 'min' | 'max' | 'percentage' | 'concat' | 'expression' | 'weighted_score' | 'pass_fail_result';
  /** Section key to aggregate over (required for repeatable sections) */
  source_section?: string;
  /** Field key within the section to aggregate */
  source_field?: string;
  /** For percentage type: count occurrences of this value */
  count_value?: string | boolean;
  /** Unit label for display (e.g. "MΩ", "%") */
  unit?: string;
  /** Where to show this computed field */
  display_in?: 'summary' | 'section' | 'hidden';
  /** Decimal precision for rounding */
  precision?: number;
  /** Optional format string: 'percentage' adds %, 'currency' adds R */
  format?: 'percentage' | 'currency' | 'plain';
  /** Condition to filter source rows */
  filter?: {
    field: string;
    operator: '=' | '!=' | '>' | '<' | '>=' | '<=';
    value: string | number | boolean;
  };
  /** Expression string for 'expression' type: e.g. "{field_a} + {field_b}" */
  expression?: string;
  /** Whether this computed field should be rendered visibly in the form */
  visible?: boolean;
  /** Weighted scoring sources — for 'weighted_score' type */
  weighted_sources?: WeightedSource[];
  /** Pass/fail conditions — for 'pass_fail_result' type.
   *  References another computed field key to evaluate against a threshold. */
  pass_fail_config?: {
    /** The computed field key whose value to evaluate (e.g. a weighted_score field) */
    source_computed_key?: string;
    /** Direct field key to evaluate (alternative to source_computed_key) */
    source_field?: string;
    /** Conditions evaluated in order; first match wins */
    conditions: PassFailCondition[];
    /** Default result when no condition matches */
    default_label?: string;
  };
}

export interface ComputedFieldResult {
  key: string;
  label: string;
  value: number | string | null;
  display: string;
  unit?: string;
  type: string;
}

interface FormSection {
  key: string;
  fields: { key: string; type: string; [k: string]: unknown }[];
  is_repeatable?: boolean;
  [k: string]: unknown;
}

// ============================================================================
// ENGINE
// ============================================================================

/**
 * Evaluate all computed fields against current form data.
 * 
 * @param computedFieldDefs - Array of computed field definitions from form_schema
 * @param formData - Current form data (may contain arrays for repeatable sections)
 * @param sections - Form sections (needed to resolve field types)
 * @returns Array of computed field results
 */
export function evaluateComputedFields(
  computedFieldDefs: ComputedFieldDef[],
  formData: Record<string, unknown>,
  sections: FormSection[]
): ComputedFieldResult[] {
  if (!computedFieldDefs?.length) return [];

  // Evaluate all fields in order; pass_fail_result may reference earlier computed results
  const results: ComputedFieldResult[] = [];
  for (const def of computedFieldDefs) {
    try {
      const result = evaluateSingleField(def, formData, sections, computedFieldDefs, results);
      results.push(result);
    } catch (err) {
      console.warn(`[ComputedFields] Error evaluating "${def.key}":`, err);
      results.push({
        key: def.key,
        label: def.label,
        value: null,
        display: '—',
        unit: def.unit,
        type: def.type,
      });
    }
  }
  return results;
}

function evaluateSingleField(
  def: ComputedFieldDef,
  formData: Record<string, unknown>,
  sections: FormSection[],
  computedFieldDefs?: ComputedFieldDef[],
  priorResults?: ComputedFieldResult[]
): ComputedFieldResult {
  const precision = def.precision ?? 2;
  const values = collectValues(def, formData, sections);
  let value: number | string | null = null;
  let display = '—';

  switch (def.type) {
    case 'count': {
      if (def.count_value !== undefined) {
        // Count occurrences of a specific value
        value = values.filter(v => matchesValue(v, def.count_value!)).length;
      } else {
        // Count all non-empty values
        value = values.filter(v => v !== null && v !== undefined && v !== '').length;
      }
      display = `${value}`;
      break;
    }

    case 'sum': {
      const nums = values.map(toNumber).filter(n => n !== null) as number[];
      value = nums.reduce((a, b) => a + b, 0);
      value = round(value, precision);
      display = formatValue(value, def);
      break;
    }

    case 'average': {
      const nums = values.map(toNumber).filter(n => n !== null) as number[];
      value = nums.length > 0 ? round(nums.reduce((a, b) => a + b, 0) / nums.length, precision) : null;
      display = value !== null ? formatValue(value, def) : '—';
      break;
    }

    case 'min': {
      const nums = values.map(toNumber).filter(n => n !== null) as number[];
      value = nums.length > 0 ? round(Math.min(...nums), precision) : null;
      display = value !== null ? formatValue(value, def) : '—';
      break;
    }

    case 'max': {
      const nums = values.map(toNumber).filter(n => n !== null) as number[];
      value = nums.length > 0 ? round(Math.max(...nums), precision) : null;
      display = value !== null ? formatValue(value, def) : '—';
      break;
    }

    case 'percentage': {
      // Percentage = (count matching count_value / total) * 100
      const total = values.filter(v => v !== null && v !== undefined && v !== '').length;
      const matching = def.count_value !== undefined
        ? values.filter(v => matchesValue(v, def.count_value!)).length
        : 0;
      value = total > 0 ? round((matching / total) * 100, precision) : null;
      display = value !== null ? `${value}%` : '—';
      break;
    }

    case 'concat': {
      const strs = values
        .filter(v => v !== null && v !== undefined && v !== '')
        .map(String);
      value = strs.join(', ');
      display = value || '—';
      break;
    }

    case 'expression': {
      if (def.expression) {
        value = evaluateExpression(def.expression, formData);
        if (value !== null && typeof value === 'number') {
          value = round(value, precision);
          display = formatValue(value, def);
        } else {
          display = value !== null ? String(value) : '—';
        }
      }
      break;
    }

    case 'weighted_score': {
      if (def.weighted_sources?.length) {
        value = evaluateWeightedScore(def.weighted_sources, formData, sections, precision);
        display = value !== null ? formatValue(value, def) : '—';
      }
      break;
    }

    case 'pass_fail_result': {
      const pfConfig = def.pass_fail_config;
      if (pfConfig) {
        const result = evaluatePassFailResult(pfConfig, formData, sections, computedFieldDefs || [], priorResults);
        value = result.value;
        display = result.display;
      }
      break;
    }
  }

  return {
    key: def.key,
    label: def.label,
    value,
    display,
    unit: def.unit,
    type: def.type,
  };
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Collect raw values from formData for the given computed field definition.
 * Handles both flat fields and repeatable section arrays.
 */
function collectValues(
  def: ComputedFieldDef,
  formData: Record<string, unknown>,
  sections: FormSection[]
): unknown[] {
  const sectionKey = def.source_section;
  const fieldKey = def.source_field;

  if (!sectionKey && fieldKey) {
    // Direct flat field reference
    const val = formData[fieldKey];
    return val !== undefined ? [val] : [];
  }

  if (sectionKey) {
    const sectionData = formData[sectionKey];
    const section = sections.find(s => s.key === sectionKey);

    if (Array.isArray(sectionData)) {
      // Repeatable section — iterate instances
      let rows = sectionData;

      // Apply filter if defined
      if (def.filter) {
        const filter = def.filter;
        rows = rows.filter(row => applyFilter(row as Record<string, unknown>, filter));
      }

      if (fieldKey) {
        return rows.map(row => extractFieldValue(row as Record<string, unknown>, fieldKey));
      } else {
        // Count instances
        return rows;
      }
    } else if (section) {
      // Non-repeatable section — collect fields from flat formData
      if (fieldKey) {
        const val = formData[fieldKey];
        if (def.filter && !applyFilter(formData, def.filter)) return [];
        return val !== undefined ? [val] : [];
      } else {
        // Count filled fields in section
        return section.fields.map(f => formData[f.key]).filter(v => v !== undefined && v !== null && v !== '');
      }
    }
  }

  // No section specified — scan all form data
  if (fieldKey) {
    // Look for this field key across all sections including repeatable
    const values: unknown[] = [];
    for (const section of sections) {
      const sData = formData[section.key];
      if (Array.isArray(sData)) {
        for (const row of sData) {
          const rec = row as Record<string, unknown>;
          if (rec[fieldKey] !== undefined) values.push(extractFieldValue(rec, fieldKey));
        }
      }
    }
    // Also check flat formData
    if (formData[fieldKey] !== undefined) {
      values.push(formData[fieldKey]);
    }
    return values;
  }

  return [];
}

/**
 * Extract the comparable/numeric value from a field value.
 * Handles pass_fail objects, measurement objects, etc.
 */
function extractFieldValue(row: Record<string, unknown>, fieldKey: string): unknown {
  const val = row[fieldKey];
  if (val === null || val === undefined || typeof val !== 'object') return val;

  // pass_fail fields store { result: 'pass'|'fail'|'na', comment?: string }
  if ('result' in val) {
    return (val as { result?: unknown }).result;
  }

  // measurement fields might store as raw number or { value, unit }
  if ('value' in val) {
    return (val as { value?: unknown }).value;
  }

  return val;
}

function matchesValue(actual: unknown, expected: string | boolean): boolean {
  if (actual === null || actual === undefined) return false;
  const normalizedActual =
    typeof actual === 'object' && 'result' in actual ? (actual as { result?: unknown }).result : actual;
  return String(normalizedActual).toLowerCase() === String(expected).toLowerCase();
}

function applyFilter(row: Record<string, unknown>, filter: { field: string; operator: string; value: string | number | boolean }): boolean {
  const actual = extractFieldValue(row, filter.field);
  const expected = filter.value;

  switch (filter.operator) {
    case '=': return String(actual) === String(expected);
    case '!=': return String(actual) !== String(expected);
    case '>': return toNumber(actual)! > Number(expected);
    case '<': return toNumber(actual)! < Number(expected);
    case '>=': return toNumber(actual)! >= Number(expected);
    case '<=': return toNumber(actual)! <= Number(expected);
    default: return true;
  }
}

function toNumber(val: unknown): number | null {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'number') return val;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

function round(num: number, precision: number): number {
  const factor = Math.pow(10, precision);
  return Math.round(num * factor) / factor;
}

function formatValue(value: number, def: ComputedFieldDef): string {
  const formatted = value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: def.precision ?? 2,
  });

  if (def.format === 'percentage') return `${formatted}%`;
  if (def.format === 'currency') return `R ${formatted}`;
  if (def.unit) return `${formatted} ${def.unit}`;
  return formatted;
}

// ============================================================================
// WEIGHTED SCORE EVALUATOR
// ============================================================================

/**
 * Evaluate a weighted score from multiple sources.
 * Each source has a section_key, optional field_key, a weight, and optional max_score.
 * 
 * Algorithm:
 *   For each source, compute the raw score (sum of numeric values in that section/field).
 *   If max_score is provided, normalize to a 0-100 scale: (raw / max) * 100.
 *   Multiply by the weight.
 *   Final score = sum(weighted_normalized_scores) / sum(weights).
 */
function evaluateWeightedScore(
  sources: WeightedSource[],
  formData: Record<string, unknown>,
  sections: FormSection[],
  precision: number
): number | null {
  if (!sources?.length) return null;

  let totalWeightedScore = 0;
  let totalWeight = 0;

  for (const src of sources) {
    const weight = src.weight ?? 1;
    if (weight === 0) continue;

    let rawScore = 0;
    const section = sections.find(s => s.key === src.section_key);

    if (src.field_key) {
      // Single field score
      const val = formData[src.field_key];
      const num = toNumber(val);
      if (num !== null) rawScore = num;
    } else if (section) {
      // Sum all numeric fields in the section
      const sectionData = formData[src.section_key];
      if (Array.isArray(sectionData)) {
        // Repeatable section
        for (const row of sectionData) {
          for (const field of section.fields) {
            const num = toNumber(extractFieldValue(row as Record<string, unknown>, field.key));
            if (num !== null) rawScore += num;
          }
        }
      } else {
        // Flat section
        for (const field of section.fields) {
          const num = toNumber(formData[field.key]);
          if (num !== null) rawScore += num;
        }
      }
    }

    // Normalize if max_score is defined
    const normalized = src.max_score && src.max_score > 0
      ? (rawScore / src.max_score) * 100
      : rawScore;

    totalWeightedScore += normalized * weight;
    totalWeight += weight;
  }

  if (totalWeight === 0) return null;
  return round(totalWeightedScore / totalWeight, precision);
}

// ============================================================================
// PASS / FAIL RESULT EVALUATOR
// ============================================================================

/**
 * Evaluate a pass/fail result based on conditions applied to a computed or direct value.
 * Conditions are evaluated in order; the first matching condition determines the result.
 */
function evaluatePassFailResult(
  config: NonNullable<ComputedFieldDef['pass_fail_config']>,
  formData: Record<string, unknown>,
  sections: FormSection[],
  computedFieldDefs: ComputedFieldDef[],
  priorResults?: ComputedFieldResult[],
): { value: string; display: string } {
  let sourceValue: number | null = null;

  if (config.source_computed_key) {
    // First try to find in previously-computed results
    const priorResult = priorResults?.find(r => r.key === config.source_computed_key);
    if (priorResult) {
      sourceValue = typeof priorResult.value === 'number' ? priorResult.value : toNumber(priorResult.value);
    } else {
      // Fallback: evaluate inline
      const sourceDef = computedFieldDefs.find(d => d.key === config.source_computed_key);
      if (sourceDef) {
        const sourceResult = evaluateSingleField(sourceDef, formData, sections);
        sourceValue = typeof sourceResult.value === 'number' ? sourceResult.value : toNumber(sourceResult.value);
      }
    }
  } else if (config.source_field) {
    sourceValue = toNumber(formData[config.source_field]);
  }

  if (sourceValue === null) {
    return { value: 'pending', display: config.default_label || 'Pending' };
  }

  for (const cond of config.conditions) {
    let matches = false;
    switch (cond.operator) {
      case '>=': matches = sourceValue >= cond.threshold; break;
      case '>':  matches = sourceValue > cond.threshold; break;
      case '<=': matches = sourceValue <= cond.threshold; break;
      case '<':  matches = sourceValue < cond.threshold; break;
      case '=':  matches = sourceValue === cond.threshold; break;
      case '!=': matches = sourceValue !== cond.threshold; break;
    }
    if (matches) {
      const label = cond.pass_label || 'Pass';
      return { value: label.toLowerCase(), display: label };
    }
  }

  const failLabel = config.conditions[0]?.fail_label || config.default_label || 'Fail';
  return { value: failLabel.toLowerCase(), display: failLabel };
}

// ============================================================================
// EXPRESSION EVALUATOR
// ============================================================================

/**
 * Evaluate a simple arithmetic expression with {field_key} references.
 * Supports: + - * / ( ) and numeric literals.
 * Example: "{total_hours} * {hourly_rate}"
 * Returns null if any referenced field is missing or non-numeric.
 */
function evaluateExpression(
  expression: string,
  formData: Record<string, unknown>
): number | null {
  try {
    // Replace {field_key} with actual numeric values
    const resolved = expression.replace(/\{([^}]+)\}/g, (_, fieldKey: string) => {
      const raw = formData[fieldKey.trim()];
      // Handle nested value objects (measurement, etc.)
      const val = typeof raw === 'object' && raw !== null && 'value' in raw ? (raw as { value?: unknown }).value : raw;
      const num = toNumber(val);
      if (num === null) return 'NaN';
      return String(num);
    });

    // If any reference couldn't be resolved, bail
    if (resolved.includes('NaN')) return null;

    // Validate: only allow digits, operators, parens, dots, spaces
    if (!/^[\d+\-*/().%\s]+$/.test(resolved)) return null;

    // Safe evaluation using Function constructor (no access to scope)
    const result = new Function(`"use strict"; return (${resolved});`)();

    if (typeof result !== 'number' || !isFinite(result)) return null;
    return result;
  } catch {
    return null;
  }
}
